// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { GridApi, RowDataUpdatedEvent, GridReadyEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { waste, WasteContainerDO } from '../../requests';

import Graph from './Graph';
import Unload from './Unload';
import Edit from './Edit';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  marked: number;

  data: WasteContainerDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: any;
  private mounted: boolean;
  private buttons: TableButtonsConfig;
  private gridApi: GridApi | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      // toolbar
      add: hasLevel('role', ['ADM', 'URW']),
      pCustom: [true],
      // actions
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
      graph: true,
      list: true,
      labels: {
        graph: 'Unloads',
        list: 'Unloads',
        pCustom: ['Unloads']
      }
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      marked: 0,

      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    this.loadingStart();
    waste<WasteContainerDO[]>('list')
      ?.then((resp: WasteContainerDO[]) => {
        this.setLocalState({
          data: resp,
          marked: 0,
        });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public countSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setLocalState({
      update: window.getStr(),
      marked,
    });
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'WE';
    const title: string = rowId ? `Waste-Container Edit: ${rowId}` : 'Waste-Container New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        report: 'WASTE CONTAINERS',
        title: 'WasteContainers',
        tabId: this.pid,
        notify: this.notify,
        wasteContainers: this.state.data,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (item: WasteContainerDO): void => {
    window.store.dispatch(removeTab('WE'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: WasteContainerDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.name}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        waste('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load();
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public onList = (item: WasteContainerDO): void => {
    const rowId: number = item.id;
    const tabId: string = 'WD';
    const label: string = `Unloads Container: ${rowId}`;

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: label,
      component: Unload,
      props: {
        rowId,
        report: label,
        title: label,
        tabId: this.pid,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public onPCustom1 = (): void => {
    const tabId: string = 'WD';
    const label: string = 'Unloads ALL';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: label,
      component: Unload,
      props: {
        tabId: this.pid,
        report: label,
        title: label,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public onGraph = (item: WasteContainerDO): void => {
    window.store.dispatch(removeTab('GE'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadGraph(rowId);
    }, 0);
  };

  public loadGraph = (rowId: number): void => {
    const tabId: string = 'GE';
    const label: string = `Unloads Graph: ${rowId}`;

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: label,
      component: Graph,
      props: {
        rowId,
        tabId: this.pid,
        report: label,
        title: label,
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public render(): JSX.Element {
    const { loading, marked, showGrid, data } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      company: 'Company',
      name: 'Name',
      code: 'Code',
      type: 'Type',
      model: 'Model',
      size: 'Size',
      date: 'Date',
      active: 'Active',
      minUnload: 'MIN. Unload',
      maxWeight: 'MAX. Weight',
      minTemp: 'MIN. Temp',
      maxTemp: 'MAX. Temp',
    };
    const columnsToHide: string[] = [
      'description'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'center' },
      minUnload: { textAlign: 'right' },
      maxWeight: { textAlign: 'right' },
      minTemp: { textAlign: 'right' },
      maxTemp: { textAlign: 'right' },
      active: { textAlign: 'center' },
      date: { textAlign: 'center' },
      type: { textAlign: 'left' },
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      company: { textAlign: 'left' },
      model: { textAlign: 'left' },
      size: { textAlign: 'right' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <div className="col-6 wdna-padding">
          <span>
            <b>{`Total Rows Selected: ${marked}`}</b>
          </span>
        </div>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          customColumnsDefs={customColumnsDefs}
          onAdd={this.onEdit}
          onPCustom={[this.onPCustom1]}
          onEdit={this.onEdit}
          onDelete={this.onDelete}
          onGraph={this.onGraph}
          onList={this.onList}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.countSelected}
          selection="multiple"
        />
      </div>
    );
  }
}
